/**
 * Admin → Users
 * Approve or reject sign-ups, add addresses up front, disable accounts and
 * choose who else is an admin. Admins only (the server checks too).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Menu,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AppLayout from '../components/Layout/AppLayout';
import ConfirmDialog from '../components/Common/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const TABS = [
  { key: 'pending', label: 'Waiting for approval' },
  { key: 'active', label: 'Active' },
  { key: 'disabled', label: 'Disabled' },
  { key: 'all', label: 'All' },
];

const errorText = (err, fallback) => {
  const detail = err.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join(', ');
  return detail || fallback;
};

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '—');

function StatusChip({ user }) {
  if (user.status === 'pending') return <Chip size="small" color="warning" label="Waiting for approval" />;
  if (user.status === 'disabled') return <Chip size="small" label="Disabled" />;
  if (!user.signed_up) {
    // Used the tool before passwords were added but never set one
    if (user.last_login || user.run_count) {
      return (
        <Tooltip title="Used the tool before passwords were added. They set one by creating an account with this email.">
          <Chip size="small" color="info" variant="outlined" label="No password yet" />
        </Tooltip>
      );
    }
    return <Chip size="small" color="info" variant="outlined" label="Added, not signed up yet" />;
  }
  return <Chip size="small" color="success" label="Active" />;
}

function AddUserDialog({ onClose, onAdded }) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState('user');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const { data } = await api.post('/admin/users', { email: email.trim(), full_name: fullName.trim() || null, role });
      onAdded(data);
    } catch (err) {
      setError(errorText(err, 'Could not add this user.'));
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={submit}>
        <DialogTitle>Add user</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            They can sign up with this email and use the tool straight away, without waiting for approval.
          </Typography>
          {error && <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>}
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@masholdings.com"
            required
            fullWidth
            margin="normal"
            autoFocus
          />
          <TextField
            label="Full name (optional)"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField select label="Role" value={role} onChange={(e) => setRole(e.target.value)} fullWidth margin="normal">
            <MenuItem value="user">User</MenuItem>
            <MenuItem value="admin">Admin (can manage users)</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={saving || !email.trim()}>
            {saving ? <CircularProgress size={20} /> : 'Add user'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default function AdminUsersPage() {
  const { user: me, refreshUser } = useAuth();
  const [users, setUsers] = useState(null);
  const [tab, setTab] = useState('pending');
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [confirm, setConfirm] = useState(null); // { title, message, label, run }
  const [busyId, setBusyId] = useState(null);
  const [menu, setMenu] = useState(null); // { anchor, user }

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/users');
      setUsers(data);
      // Open on Active when nobody is waiting
      setTab((t) => (t === 'pending' && !data.some((u) => u.status === 'pending') ? 'active' : t));
    } catch (err) {
      // No longer an admin (e.g. another account signed in from another tab):
      // reloading the user sends this page back to the dashboard
      if (err.response?.status === 403) refreshUser();
      else setError(errorText(err, 'Could not load users.'));
    }
  }, [refreshUser]);

  // Keep the list current: new sign-ups appear without reloading the page
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const counts = useMemo(() => {
    const c = { pending: 0, active: 0, disabled: 0, all: users?.length || 0 };
    (users || []).forEach((u) => { c[u.status] = (c[u.status] || 0) + 1; });
    return c;
  }, [users]);

  const shown = (users || []).filter((u) => tab === 'all' || u.status === tab);

  const replace = (row) => setUsers((list) => list.map((u) => (u.id === row.id ? row : u)));
  // Updates the sidebar's waiting-for-approval badge
  const changed = () => window.dispatchEvent(new Event('admin:users-changed'));

  const run = async (id, action) => {
    setBusyId(id);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(errorText(err, 'That did not work. Please try again.'));
    } finally {
      setBusyId(null);
    }
  };

  const update = (u, body) => run(u.id, async () => {
    const { data } = await api.patch(`/admin/users/${u.id}`, body);
    replace(data);
    changed();
  });

  const remove = (u) => run(u.id, async () => {
    await api.delete(`/admin/users/${u.id}`);
    setUsers((list) => list.filter((x) => x.id !== u.id));
    changed();
  });

  const ask = (title, message, label, action) => setConfirm({ title, message, label, run: action });

  const actions = (u) => {
    if (u.id === me?.id) return <Typography variant="caption" color="text.secondary">You</Typography>;
    if (u.configured_admin) {
      return (
        <Tooltip title="Set in the server settings (ADMIN_EMAILS)">
          <Typography variant="caption" color="text.secondary">Built-in admin</Typography>
        </Tooltip>
      );
    }
    const busy = busyId === u.id;
    const name = u.full_name || u.email;
    if (u.status === 'pending') {
      return (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', whiteSpace: 'nowrap' }}>
          <Button size="small" variant="contained" color="success" disabled={busy}
            onClick={() => update(u, { status: 'active' })}>Approve</Button>
          <Button size="small" color="error" disabled={busy}
            onClick={() => ask('Reject sign-up?', `${name} won't be able to sign in. They can sign up again later.`, 'Reject', () => remove(u))}>
            Reject
          </Button>
        </Box>
      );
    }
    return (
      <IconButton size="small" disabled={busy} onClick={(e) => setMenu({ anchor: e.currentTarget, user: u })}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
    );
  };

  const menuItems = (u) => {
    const name = u.full_name || u.email;
    const close = (fn) => () => { setMenu(null); fn(); };
    const items = [];
    if (u.status === 'active') {
      items.push(
        <MenuItem key="role" onClick={close(() => update(u, { role: u.role === 'admin' ? 'user' : 'admin' }))}>
          {u.role === 'admin' ? 'Remove admin' : 'Make admin'}
        </MenuItem>,
        <MenuItem key="disable" sx={{ color: 'error.main' }}
          onClick={close(() => ask('Disable account?', `${name} will be signed out and can't sign in until an admin enables the account again.`, 'Disable', () => update(u, { status: 'disabled' })))}>
          Disable
        </MenuItem>,
      );
    } else {
      items.push(<MenuItem key="enable" onClick={close(() => update(u, { status: 'active' }))}>Enable</MenuItem>);
    }
    items.push(
      u.run_count === 0 ? (
        <MenuItem key="delete" sx={{ color: 'error.main' }}
          onClick={close(() => ask('Remove user?', `${name} will be removed from the list.`, 'Remove', () => remove(u)))}>
          Remove
        </MenuItem>
      ) : (
        <Tooltip key="delete" title="Has runs in History, so it can only be disabled" placement="left">
          <span><MenuItem disabled>Remove</MenuItem></span>
        </Tooltip>
      ),
    );
    return items;
  };

  return (
    <AppLayout>
      <Box>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" gutterBottom fontWeight={600}>
              Users
            </Typography>
            <Typography variant="body1" color="text.secondary">
              New sign-ups wait here until an admin approves them. Users you add can sign up and start straight away.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<PersonAddAlt1Icon />}
            onClick={() => setAdding(true)}
            sx={{ bgcolor: '#D32F2F', '&:hover': { bgcolor: '#B71C1C' }, boxShadow: 'none', flexShrink: 0 }}
          >
            Add user
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {!users && !error && <CircularProgress />}

        {users && (
          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ px: 1, borderBottom: 1, borderColor: 'divider' }}>
              {TABS.map((t) => (
                <Tab
                  key={t.key}
                  value={t.key}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {t.label}
                      <Chip size="small" label={counts[t.key] || 0}
                        color={t.key === 'pending' && counts.pending ? 'warning' : 'default'} />
                    </Box>
                  }
                />
              ))}
            </Tabs>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Signed up</TableCell>
                    <TableCell>Last sign-in</TableCell>
                    <TableCell align="right">Runs</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shown.map((u) => (
                    <TableRow key={u.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{u.full_name || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                      </TableCell>
                      <TableCell>
                        {u.role === 'admin'
                          ? <Chip size="small" color="secondary" label="Admin" />
                          : <Typography variant="body2">User</Typography>}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}><StatusChip user={u} /></TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{u.signed_up ? formatDate(u.created_at) : '—'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(u.last_login)}</TableCell>
                      <TableCell align="right">{u.run_count}</TableCell>
                      <TableCell align="right">{actions(u)}</TableCell>
                    </TableRow>
                  ))}
                  {shown.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                        {tab === 'pending' ? 'Nobody is waiting for approval.' : 'No users here.'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>

      {adding && (
        <AddUserDialog
          onClose={() => setAdding(false)}
          onAdded={(row) => {
            setUsers((list) => [row, ...list.filter((u) => u.id !== row.id)]);
            setTab('active');
            setAdding(false);
            window.dispatchEvent(new Event('admin:users-changed'));
          }}
        />
      )}

      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}>
        {menu && menuItems(menu.user)}
      </Menu>

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.label}
        onClose={() => setConfirm(null)}
        onConfirm={() => { confirm.run(); setConfirm(null); }}
      />
    </AppLayout>
  );
}
